/**
 * Specialty Field Components
 * 
 * These are complex fields that require data from the context:
 * - client_* fields (client data display)
 * - treatment_plan_goals
 * - consent_affirmation
 * - client_diagnoses_selector
 * - group_attendance, group_member_notes
 * - matrix, signature
 * - service_dropdown, location_dropdown, provider_dropdown, group_dropdown
 * 
 * Supports 'edit' and 'view' modes for each interactive field.
 * 
 * IMPORTANT: Uses DebouncedInput and DebouncedTextarea to prevent UI "jumps"
 * during typing. These components maintain local state and only propagate
 * changes to the parent after a delay.
 */

import React, { useState, useRef, useEffect } from 'react';
import { formatDateMDY } from '../../../../utils/dateFormat';
import { matrixBaseRows, matrixCellValue } from '../../../../utils/matrixRows';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Button } from '../../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Textarea } from '../../../ui/textarea';
import { SignatureDisplay } from '../../../shared/SignatureDisplay';
import { DebouncedInput, DebouncedTextarea } from '../../../ui/debounced-textarea';
import { Plus, X, Target, Building, ChevronDown, Edit, MoreVertical, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../ui/dropdown-menu';
import { toast } from 'sonner';
import { CreateTaskFieldWidget } from '../../../shared/CreateTaskFieldWidget';
import { AssessmentQuickSend, RecentAssessments } from '../../../features/AssessmentQuickSend';
// PHI-fix May 2026 — see component for full doc.
import { SnapshotMismatchBanner } from '../../../shared/SnapshotMismatchBanner';
import { MostRecentVitalsCard, AddVitalsDialog, parseVitalsSnapshot } from '../../../shared/VitalsFieldWidget';
import { AddAllergyInline } from '../../../features/AllergiesSection';
import { AddContactNoteField, parseAddedContacts } from './AddContactNoteField';

// Helper to format state names for consent language (e.g., "Commonwealth of Massachusetts")
const formatStateForConsent = (stateName) => {
  // Handle null, undefined, or empty string - default to Massachusetts
  const effectiveState = (stateName && stateName.trim() !== '') ? stateName.trim() : 'Massachusetts';
  
  const commonwealthStates = {
    'Massachusetts': 'the Commonwealth of Massachusetts',
    'Pennsylvania': 'the Commonwealth of Pennsylvania',
    'Virginia': 'the Commonwealth of Virginia',
    'Kentucky': 'the Commonwealth of Kentucky',
  };
  
  return commonwealthStates[effectiveState] || effectiveState;
};
/**
 * AutoGrowTextarea - A textarea that automatically grows with content
 */
const AutoGrowTextarea = ({ value, onChange, className, placeholder, disabled }) => {
  const textareaRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || '');
  
  // Sync with external value
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  
  // Auto-resize on value change
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [localValue]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };
  
  return (
    <textarea
      ref={textareaRef}
      className={className}
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      rows={1}
    />
  );
};

/**
 * View mode helper components - styled read-only display
 */
const ViewValue = ({ children, className = '' }) => (
  <div className={`text-sm text-gray-800 py-1 ${className}`}>
    {children || <span className="text-gray-400 italic">Not provided</span>}
  </div>
);

const ViewPill = ({ children, variant = 'default' }) => {
  const baseClasses = 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium';
  const variantClasses = variant === 'selected' 
    ? 'bg-blue-100 text-blue-800 border border-blue-200'
    : 'bg-gray-100 text-gray-700 border border-gray-200';
  return <span className={`${baseClasses} ${variantClasses}`}>{children}</span>;
};

// === MEDICATION PLAN helpers (Jun 2026) ===
const MED_PLAN_ACTIONS = [
  { value: 'continue', label: 'Continue', cls: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'discontinue', label: 'Discontinue', cls: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'hold', label: 'Hold', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'start', label: 'Start', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
];

export const parseMedPlanRows = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch (e) { return []; }
  }
  return [];
};

const newMedPlanRowId = () => `mp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const MedicationPlanTable = ({ field, rows, sessionDate, isViewMode, disabled, onRowsChange }) => {
  const actionMeta = (a) => MED_PLAN_ACTIONS.find(m => m.value === String(a || '').toLowerCase()) || MED_PLAN_ACTIONS[3];
  const headerCells = (
    <tr className="border-b border-teal-200">
      <th className="text-left p-1 font-semibold text-teal-800">Action</th>
      <th className="text-left p-1 font-semibold text-teal-800">Medication</th>
      <th className="text-left p-1 font-semibold text-teal-800">Dose</th>
      <th className="text-left p-1 font-semibold text-teal-800">Details</th>
      <th className="text-left p-1 font-semibold text-teal-800">Notes / Reason</th>
      <th className="text-left p-1 font-semibold text-teal-800">Date</th>
      {!isViewMode && <th className="w-8"></th>}
    </tr>
  );
  if (isViewMode) {
    return (
      <div className="border rounded p-2 bg-teal-50/50 border-teal-200" data-testid={`med-plan-view-${field.id}`}>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No medication plan entries</p>
        ) : (
          <table className="w-full text-xs">
            <thead>{headerCells}</thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id || idx} className="border-b border-teal-100 last:border-0">
                  <td className="p-1"><span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${actionMeta(row.action).cls}`}>{actionMeta(row.action).label}</span></td>
                  <td className="p-1">{row.medication_name}</td>
                  <td className="p-1">{row.dosage}</td>
                  <td className="p-1">{row.details}</td>
                  <td className="p-1">
                    {row.reason}
                    {row.applied_error && <span className="text-red-600 text-[10px] block" title={row.applied_error}>⚠ {row.applied_error}</span>}
                  </td>
                  <td className="p-1 whitespace-nowrap">{String(row.date || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }
  const updateRow = (idx, patch) => onRowsChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => onRowsChange(rows.filter((_, i) => i !== idx));
  const addRow = () => onRowsChange([...rows, {
    id: newMedPlanRowId(), action: 'start', medication_id: null,
    medication_name: '', dosage: '', details: '', reason: '', date: sessionDate,
  }]);
  return (
    <div className="border rounded p-2 bg-teal-50/50 border-teal-200" data-testid={`med-plan-edit-${field.id}`}>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 italic px-1 py-2">No plan entries yet — use the ⋮ menu on a medication above, or add a row manually.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>{headerCells}</thead>
          <tbody>
            {rows.map((row, idx) => (
              row.applied ? (
                <tr key={row.id || idx} className="border-b border-teal-100 last:border-0 align-top bg-teal-50/70" data-testid={`med-plan-applied-row-${field.id}-${idx}`}>
                  <td className="p-1 w-28">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${actionMeta(row.action).cls}`}>{actionMeta(row.action).label}</span>
                  </td>
                  <td className="p-1">{row.medication_name}</td>
                  <td className="p-1 w-24">{row.dosage}</td>
                  <td className="p-1">{row.details}</td>
                  <td className="p-1">
                    {row.reason}
                    {row.applied_error && <span className="text-red-600 text-[10px] block" title={row.applied_error}>⚠ {row.applied_error}</span>}
                  </td>
                  <td className="p-1 w-32 whitespace-nowrap">{String(row.date || '').slice(0, 10)}</td>
                  <td className="p-1 w-8">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200" title={`Applied to the chart${row.applied_at ? ` on ${String(row.applied_at).slice(0, 10)}` : ''}`} data-testid={`med-plan-applied-badge-${field.id}-${idx}`}>✓</span>
                  </td>
                </tr>
              ) : (
              <tr key={row.id || idx} className="border-b border-teal-100 last:border-0 align-top">
                <td className="p-1 w-28">
                  <select
                    value={String(row.action || 'start').toLowerCase()}
                    onChange={(e) => updateRow(idx, { action: e.target.value })}
                    disabled={disabled}
                    className={`text-[11px] border rounded px-1 py-1 ${actionMeta(row.action).cls}`}
                    data-testid={`med-plan-action-${field.id}-${idx}`}
                  >
                    {MED_PLAN_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </td>
                <td className="p-1"><DebouncedInput value={row.medication_name || ''} onChange={(e) => updateRow(idx, { medication_name: e.target.value })} disabled={disabled} className="h-6 text-xs" placeholder="Medication" data-testid={`med-plan-name-${field.id}-${idx}`} /></td>
                <td className="p-1 w-24"><DebouncedInput value={row.dosage || ''} onChange={(e) => updateRow(idx, { dosage: e.target.value })} disabled={disabled} className="h-6 text-xs" placeholder="Dose" data-testid={`med-plan-dose-${field.id}-${idx}`} /></td>
                <td className="p-1"><DebouncedInput value={row.details || ''} onChange={(e) => updateRow(idx, { details: e.target.value })} disabled={disabled} className="h-6 text-xs" placeholder="Frequency / details" data-testid={`med-plan-details-${field.id}-${idx}`} /></td>
                <td className="p-1"><DebouncedInput value={row.reason || ''} onChange={(e) => updateRow(idx, { reason: e.target.value })} disabled={disabled} className="h-6 text-xs" placeholder={String(row.action || '').toLowerCase() === 'hold' ? 'Hold reason / period' : 'Notes / reason'} data-testid={`med-plan-reason-${field.id}-${idx}`} /></td>
                <td className="p-1 w-32"><Input type="date" value={String(row.date || '').slice(0, 10)} onChange={(e) => updateRow(idx, { date: e.target.value })} disabled={disabled} className="h-6 text-xs" data-testid={`med-plan-date-${field.id}-${idx}`} /></td>
                <td className="p-1 w-8">
                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => removeRow(idx)} disabled={disabled} data-testid={`med-plan-remove-${field.id}-${idx}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
              )
            ))}
          </tbody>
        </table>
      )}
      <Button type="button" variant="outline" size="sm" className="mt-2 border-teal-300 text-teal-700 hover:bg-teal-50" onClick={addRow} disabled={disabled} data-testid={`med-plan-add-row-${field.id}`}>
        <Plus className="h-4 w-4 mr-1" />
        Add Row
      </Button>
      <p className="text-[10px] text-gray-400 mt-1">Applied to the client's medication list when this note is signed.</p>
    </div>
  );
};

/**
 * Render a specialty field based on its type
 * Returns null if field type is not a specialty field
 */
export const renderSpecialtyField = (field, index, fieldStyle, context) => {
  const { 
    selectedClient,
    clients = [],
    selectedNoteClients = [],
    clientDiagnoses = [],
    clientVitals = [],
    users = [],
    appointments = [],
    newNoteFormData = {},
    handleFieldChange,
    disabled,
    mode = 'edit',
    linkedGoals = [],
    telehealthConsent,
    setTelehealthConsent,
    serviceTypes = [],
    locations = [],
    providers = [],
    allGroups = [],
    isGroupNote = false,
    groupMembers = [],
    groupAttendance = {},
    groupMemberNotes = {},
    setShowDiagnosisForm,
    setShowMedicationForm,
    setEditingMedication,
    setShowLinkGoalsDialog,
    fetchTreatmentPlanGoals,
    setLinkedGoals,
    setSelectedClient,
    organizations = [],
    posCodes = [],
    currentUser = {},
    // PHI-fix May 2026 — needed by ``SnapshotMismatchBanner`` to
    // identify which note triggered the mismatch event.
    selectedNote = null,
    // Medication Plan bridge (Jun 2026)
    allFields = [],
    setMedPlanCapture,
    setMedSnapshotCapture,
  } = context;
  
  const isViewMode = mode === 'view';

  // Add Contact from note (Jun 2026) — button-only field; each add
  // POSTs to the chart AND stamps a snapshot into the note value.
  if (field.type === 'add_contacts') {
    const acClientId = selectedNoteClients?.[0] || selectedClient?.id;
    const acRaw = newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : field.value;
    const acAppend = (snap) => {
      handleFieldChange(prev => ({
        ...(prev || {}),
        [field.id]: JSON.stringify([
          ...parseAddedContacts(prev && prev[field.id] !== undefined ? prev[field.id] : acRaw),
          snap,
        ]),
      }));
    };
    return (
      <AddContactNoteField
        key={index}
        field={field}
        fieldStyle={fieldStyle}
        clientId={acClientId}
        value={acRaw}
        onAppend={acAppend}
        isViewMode={isViewMode}
        disabled={disabled}
      />
    );
  }

  // Set a Reminder — signer picks an offset; reminder is scheduled on sign.
  if (field.type === 'set_a_reminder') {
    const cfg = field.config || {};
    const presets = { '1m': '1 month', '3m': '3 months', '6m': '6 months', '9m': '9 months', '1y': '1 year', '30d': '30 days', '60d': '60 days' };
    const allowed = (cfg.allowed_offsets && cfg.allowed_offsets.length ? cfg.allowed_offsets : ['1m', '3m', '6m', '9m', '1y']);
    let value = newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : field.value;
    if (typeof value === 'string' && value.startsWith('{')) { try { value = JSON.parse(value); } catch (e) { value = null; } }
    value = (value && typeof value === 'object') ? value : {};
    const anchorLabel = cfg.offset_anchor === 'signed_date' ? 'signed date' : 'date of service';
    const anchorRaw = cfg.offset_anchor === 'signed_date'
      ? new Date().toISOString().slice(0, 10)
      : (newNoteFormData?.date_of_service || newNoteFormData?.session_date || selectedNote?.date_of_service || selectedNote?.session_date || new Date().toISOString().slice(0, 10));
    const calcDate = (offset, customDate) => {
      try {
        const [y, m, d] = String(anchorRaw).slice(0, 10).split('-').map(Number);
        const base = new Date(y, m - 1, d);
        let out = new Date(base);
        if (offset === 'custom') { if (!customDate) return null; const [cy, cm, cd] = customDate.split('-').map(Number); out = new Date(cy, cm - 1, cd); }
        else if (offset === '30d') out.setDate(out.getDate() + 30);
        else if (offset === '60d') out.setDate(out.getDate() + 60);
        else if (offset === '1m') out.setMonth(out.getMonth() + 1);
        else if (offset === '3m') out.setMonth(out.getMonth() + 3);
        else if (offset === '6m') out.setMonth(out.getMonth() + 6);
        else if (offset === '9m') out.setMonth(out.getMonth() + 9);
        else if (offset === '1y') out.setFullYear(out.getFullYear() + 1);
        else return null;
        return `${String(out.getMonth() + 1).padStart(2, '0')}/${String(out.getDate()).padStart(2, '0')}/${out.getFullYear()}`;
      } catch (e) { return null; }
    };
    const clientName = selectedClient?.full_name || 'the client';
    const target = calcDate(value.offset, value.custom_date);
    const offsetLabel = value.offset === 'custom' ? 'custom date' : presets[value.offset];
    const preview = value.offset && target ? (
      (cfg.output_types || []).includes('draft') && cfg.draft_config?.target_template_name
        ? `Creates a draft ${cfg.draft_config.target_template_name} for ${clientName} on ${target} (${offsetLabel} from ${anchorLabel}).`
        : `Reminder scheduled for ${target} (${offsetLabel} from ${anchorLabel}).`
    ) : null;

    if (isViewMode) {
      return (
        <div key={index} style={fieldStyle} data-testid={`view-reminder-${field.id}`}>
          <Label className="text-xs font-semibold text-gray-600">{field.label || 'Follow-Up Reminder'}</Label>
          {cfg.helper_text && <p className="text-xs text-gray-500 italic mt-0.5">{cfg.helper_text}</p>}
          <p className="text-sm mt-1">{preview || <span className="text-gray-400 italic">No reminder set</span>}</p>
        </div>
      );
    }
    return (
      <div key={index} style={fieldStyle} className="border rounded-md p-3 bg-amber-50/60 border-amber-200" data-testid={`reminder-field-${field.id}`}>
        <Label className="text-sm font-medium">
          {field.label || 'Follow-Up Reminder'}
          {field.required ? <span className="text-red-500 ml-1">*</span> : <span className="text-xs text-gray-400 ml-2">(optional)</span>}
        </Label>
        {cfg.helper_text && <p className="text-xs text-gray-500 italic mt-0.5" data-testid={`reminder-helper-${field.id}`}>{cfg.helper_text}</p>}
        <p className="text-xs text-gray-600 mt-0.5 mb-2">Remind me in…</p>
        <div className="flex flex-wrap gap-1.5">
          {allowed.map((k) => (
            <Button key={k} type="button" size="sm" variant={value.offset === k ? 'default' : 'outline'}
              className="h-7 px-2.5 text-xs" disabled={disabled}
              onClick={() => handleFieldChange && handleFieldChange(prev => {
                const cur = (prev || {})[field.id];
                const curObj = (cur && typeof cur === 'object') ? cur : {};
                return { ...(prev || {}), [field.id]: { ...curObj, offset: k, custom_date: undefined } };
              })}
              data-testid={`reminder-offset-${k}-${field.id}`}>
              {presets[k] || k}
            </Button>
          ))}
          {cfg.allow_custom !== false && (
            <Button type="button" size="sm" variant={value.offset === 'custom' ? 'default' : 'outline'}
              className="h-7 px-2.5 text-xs" disabled={disabled}
              onClick={() => handleFieldChange && handleFieldChange(prev => {
                const cur = (prev || {})[field.id];
                const curObj = (cur && typeof cur === 'object') ? cur : {};
                return { ...(prev || {}), [field.id]: { ...curObj, offset: 'custom', custom_date: curObj.custom_date || '' } };
              })}
              data-testid={`reminder-offset-custom-${field.id}`}>
              Custom…
            </Button>
          )}
          {value.offset && !field.required && (
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs text-gray-500" disabled={disabled}
              onClick={() => handleFieldChange && handleFieldChange(prev => ({ ...(prev || {}), [field.id]: '' }))}
              data-testid={`reminder-skip-${field.id}`}>
              Skip
            </Button>
          )}
        </div>
        {value.offset === 'custom' && (
          <Input type="date" className="mt-2 h-8 w-44 text-xs bg-white" disabled={disabled}
            value={value.custom_date || ''}
            onChange={(e) => handleFieldChange && handleFieldChange(prev => {
              const cur = (prev || {})[field.id];
              const curObj = (cur && typeof cur === 'object') ? cur : {};
              return { ...(prev || {}), [field.id]: { ...curObj, offset: 'custom', custom_date: e.target.value } };
            })}
            data-testid={`reminder-custom-date-${field.id}`} />
        )}
        {preview && (
          <p className="text-xs text-amber-800 mt-2 font-medium" data-testid={`reminder-preview-${field.id}`}>{preview}</p>
        )}
      </div>
    );
  }
  
  // Send Assessment — quick-send widget (audit fix, Jun 2026). Action-only
  // field: renders the sender in edit mode, a subtle stub in view mode.
  if (field.type === 'send_assessment') {
    const saClientId = selectedNote?.client_id || selectedClient?.id
      || (Array.isArray(selectedNoteClients) && selectedNoteClients.length === 1 ? selectedNoteClients[0] : null);
    if (isViewMode) {
      return (
        <div key={index} style={fieldStyle} data-testid={`view-send-assessment-${field.id}`}>
          <Label className="text-xs font-semibold text-gray-600">{field.label || 'Send Assessment'}</Label>
          <div className="mt-1 p-2 rounded border border-purple-100 bg-purple-50/30">
            <p className="text-[11px] uppercase tracking-wide text-purple-700 font-semibold mb-1">Recent assessments (30 days)</p>
            {saClientId ? <RecentAssessments clientId={saClientId} /> : (
              <p className="text-xs text-gray-400 italic">No client linked</p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div key={index} style={fieldStyle}>
        <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/50 space-y-2"
          data-testid={`send-assessment-field-${field.id}`}>
          {field.label && <Label className="text-sm font-medium">{field.label}</Label>}
          <p className="text-xs text-gray-600">
            {field.description || 'Optionally send an assessment to this client (email + portal). Not required to sign or submit this note.'}
          </p>
          {saClientId ? (
            <>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-purple-700 font-semibold mb-1">Recent assessments (30 days)</p>
                <RecentAssessments clientId={saClientId} />
              </div>
              <AssessmentQuickSend clientId={saClientId} />
            </>
          ) : (
            <p className="text-xs text-gray-400 italic">Select a single client to send an assessment.</p>
          )}
        </div>
      </div>
    );
  }
  
  // Helper to handle field changes
  const onChange = (value) => {
    if (handleFieldChange) {
      handleFieldChange(prev => ({ ...prev, [field.id]: value }));
    }
  };
  
  const value = newNoteFormData[field.id] || '';
  
  // === CLIENT DATA FIELDS (always read-only, same in both modes) ===
  
  // CRITICAL: For note client data, use the note's client (selectedNoteClients), NOT selectedClient
  // selectedClient is the UI state for the currently viewed chart, which may differ from the note's client
  const noteClientId = selectedNoteClients?.[0];
  const clientFromList = noteClientId ? clients.find(c => c.id === noteClientId) : null;
  const noteClient = clientFromList || selectedClient;
  
  // Debug logging for prefill issues
  if (field.type?.startsWith('client_') && !noteClient) {
    console.warn('[FormField] No noteClient found for prefill:', {
      fieldType: field.type,
      noteClientId,
      clientsCount: clients?.length,
      selectedClientId: selectedClient?.id,
      selectedClientName: selectedClient?.full_name
    });
  }
  
  // === create_task FIELD (Feb 2026 — reschedule-request flow) =====
  // The chart "Add Note" dialog renders fields via FormFieldRenderer,
  // which delegates here for specialty fields.  Without this branch
  // the field type falls through all three dispatcher levels and
  // returns null (silent drop).  Placed AFTER `noteClient` is
  // initialised so we can pass the note's actual client to the
  // widget (which it uses to scope assignee suggestions / context).
  if (field.type === 'create_task') {
    return (
      <div key={index} style={fieldStyle}>
        <CreateTaskFieldWidget
          field={field}
          value={newNoteFormData[field.id] || {}}
          onChange={(next) => {
            if (handleFieldChange) {
              handleFieldChange(prev => ({ ...(prev || {}), [field.id]: next }));
            }
          }}
          client={noteClient || selectedClient}
          readOnly={isViewMode}
        />
      </div>
    );
  }
  
  if (field.type === 'client_name') {
    // Check for saved value (frozen in time). Guard against a UUID having
    // been stored as the value (Jun 2026 bug: client_id leaked into the
    // field value) — fall back to the live client name instead.
    const rawSaved = field.value || newNoteFormData[field.id];
    const isUuid = typeof rawSaved === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawSaved.trim());
    const savedValue = isUuid ? null : rawSaved;
    const displayValue = savedValue || noteClient?.full_name || 'Client';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayValue}</ViewValue>
        ) : (
          <Input value={displayValue} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_dob') {
    // Check for saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    let displayDob;
    if (savedValue) {
      displayDob = savedValue;
    } else {
      const dobValue = noteClient?.dob || noteClient?.date_of_birth;
      displayDob = dobValue ? formatDateMDY(dobValue) : 'N/A';
    }
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayDob}</ViewValue>
        ) : (
          <Input value={displayDob} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_age') {
    // Check for saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    let displayAge;
    if (savedValue) {
      displayAge = savedValue;
    } else {
      const dobValue = noteClient?.dob || noteClient?.date_of_birth;
      const age = dobValue
        ? Math.floor((new Date() - new Date(dobValue)) / (365.25 * 24 * 60 * 60 * 1000)) 
        : null;
      displayAge = age !== null ? `${age} years old` : 'N/A';
    }
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayAge}</ViewValue>
        ) : (
          <Input value={displayAge} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_email') {
    // Check for saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    const displayValue = savedValue || noteClient?.email || 'N/A';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayValue}</ViewValue>
        ) : (
          <Input value={displayValue} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_phone') {
    // Check for saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    const displayValue = savedValue || noteClient?.phone || 'N/A';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayValue}</ViewValue>
        ) : (
          <Input value={displayValue} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_address') {
    // Check for saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    let displayAddress;
    if (savedValue) {
      displayAddress = savedValue;
    } else {
      displayAddress = noteClient 
        ? [noteClient.address || noteClient.street || noteClient.address_line1, noteClient.address_line2, noteClient.city, noteClient.state, noteClient.zipcode || noteClient.zip || noteClient.zip_code]
            .filter(Boolean).join(', ') 
        : 'N/A';
    }
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayAddress}</ViewValue>
        ) : (
          <Input value={displayAddress} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_intake_date') {
    const intakeDate = noteClient?.intake_completed_at || noteClient?.created_at || 'N/A';
    const displayDate = intakeDate !== 'N/A' ? formatDateMDY(intakeDate) : 'N/A';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{displayDate}</ViewValue>
        ) : (
          <Input value={displayDate} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_last_appointment') {
    // Get client ID from multiple sources
    const clientId = selectedClient?.id || selectedNoteClients?.[0];
    
    // Helper to format ISO strings nicely
    const formatDateValue = (val) => {
      if (typeof val === 'string' && val.includes('T')) {
        try {
          const date = new Date(val);
          return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) { return val; }
      }
      return val;
    };
    
    // First check if we have a saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    if (savedValue && savedValue !== 'No previous appointments') {
      const displayValue = formatDateValue(savedValue);
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          {isViewMode ? (
            <ViewValue>{displayValue}</ViewValue>
          ) : (
            <Input value={displayValue} disabled className="bg-gray-100" />
          )}
        </div>
      );
    }
    
    // Find all past appointments for this client (individual or group)
    const now = new Date();
    const clientAppts = appointments.filter(a => {
      // Check if this appointment is for this client
      const isClientAppt = a.client_id === clientId || 
        (a.client_ids && a.client_ids.includes(clientId));
      // Check if it's in the past
      const isPast = new Date(a.start_time) < now;
      // Exclude cancelled appointments
      const notCancelled = a.status !== 'cancelled' && a.status !== 'client_cancelled';
      return isClientAppt && isPast && notCancelled;
    });
    
    const lastAppt = clientAppts.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))[0];
    const lastApptDisplay = lastAppt 
      ? new Date(lastAppt.start_time).toLocaleDateString() + ' ' + new Date(lastAppt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : 'No previous appointments';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{lastApptDisplay}</ViewValue>
        ) : (
          <Input value={lastApptDisplay} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  if (field.type === 'client_next_appointment') {
    // Get client ID from multiple sources
    const clientId = selectedClient?.id || selectedNoteClients?.[0];
    
    // Helper to format ISO strings nicely
    const formatDateValue = (val) => {
      if (typeof val === 'string' && val.includes('T')) {
        try {
          const date = new Date(val);
          return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) { return val; }
      }
      return val;
    };
    
    // First check if we have a saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    if (savedValue && savedValue !== 'No upcoming appointments') {
      const displayValue = formatDateValue(savedValue);
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          {isViewMode ? (
            <ViewValue>{displayValue}</ViewValue>
          ) : (
            <Input value={displayValue} disabled className="bg-gray-100" />
          )}
        </div>
      );
    }
    
    const futureAppts = appointments.filter(a => {
      const isClientAppt = a.client_id === clientId || 
        (a.client_ids && a.client_ids.includes(clientId));
      const isFuture = new Date(a.start_time) > new Date();
      const notCancelled = a.status !== 'cancelled' && a.status !== 'client_cancelled';
      return isClientAppt && isFuture && notCancelled;
    });
    const nextAppt = futureAppts.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
    const nextApptDisplay = nextAppt 
      ? new Date(nextAppt.start_time).toLocaleDateString() + ' ' + new Date(nextAppt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : 'No upcoming appointments';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{nextApptDisplay}</ViewValue>
        ) : (
          <Input value={nextApptDisplay} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  // === CLIENT MEDICATIONS ===
  // "Frozen in time" - use saved snapshot from note if viewing/editing, live data only for new notes
  
  if (field.type === 'client_medications') {
    const { clientMedications = [], API = '' } = context;
    
    // PHI-fix May 2026 — derive the note's client id BEFORE any
    // medication lookup so we can scope BOTH the live-data fallback
    // and the saved-snapshot validation to the correct patient.
    // The production bug ("note showing another person's meds") came
    // from this renderer falling back to ``clientMedications`` (global
    // state for the LAST opened chart) without checking client_id.
    const clientId = selectedNoteClients?.[0] || selectedClient?.id;
    
    // Check for saved snapshot data in the note (field.value)
    let medicationsToShow = [];
    const savedValue = field.value || newNoteFormData[field.id];
    let snapshotLooksMismatched = false;
    
    if (savedValue) {
      // Use saved snapshot from note
      let parsed = [];
      if (typeof savedValue === 'string') {
        try {
          parsed = JSON.parse(savedValue);
        } catch (e) {
          parsed = [];
        }
      } else if (Array.isArray(savedValue)) {
        parsed = savedValue;
      }
      
      // Defense-in-depth: if the saved snapshot carries explicit
      // client_id stamps on the meds (newer notes do; legacy ones
      // may not), and ALL of them belong to a different client than
      // this note, we've got a corrupted snapshot — flag it for the
      // banner below and refuse to display it.  Legacy snapshots
      // without client_id stamps are still rendered (best-effort
      // backwards compat).
      if (clientId && Array.isArray(parsed) && parsed.length > 0) {
        const stamped = parsed.filter(m => m && m.client_id);
        if (stamped.length > 0 && stamped.every(m => m.client_id !== clientId)) {
          snapshotLooksMismatched = true;
          medicationsToShow = [];
        } else {
          medicationsToShow = parsed;
        }
      } else {
        medicationsToShow = parsed;
      }
    } else {
      // No saved data - use live client medications (for new note creation)
      // PHI-fix: MUST filter by client_id so we don't display the
      // last-opened chart's meds on a different patient's note.
      medicationsToShow = (clientMedications || []).filter(
        m => m.is_active === true && (!clientId || m.client_id === clientId)
      );
    }
    
    const isSnapshot = !!savedValue && !snapshotLooksMismatched;
    
    // Get client (for handlers)
    const medicationClient = clients.find(c => c.id === clientId);

    // Medication Plan bridge (Jun 2026) — three-dot actions route rows
    // into a medication_plan field placed on the same template.
    const planField = (allFields || []).find(f => (f.type || f.field_type) === 'medication_plan');
    const planSessionDate = String(newNoteFormData?.date_of_service || newNoteFormData?.session_date || selectedNote?.date_of_service || selectedNote?.session_date || new Date().toISOString()).slice(0, 10);
    const readPlanRows = () => parseMedPlanRows(
      planField && newNoteFormData[planField.id] !== undefined ? newNoteFormData[planField.id] : planField?.value
    );
    const appendPlanRows = (newRows) => {
      if (!planField) {
        toast.error('This template has no Medication Plan field. Add one in the template builder first.');
        return false;
      }
      // Updater form: V2/legacy bridges drop (fieldId, value) calls from specialty fields.
      handleFieldChange(prev => ({
        ...(prev || {}),
        [planField.id]: JSON.stringify([
          ...parseMedPlanRows(prev && prev[planField.id] !== undefined ? prev[planField.id] : readPlanRows()),
          ...newRows,
        ]),
      }));
      return true;
    };
    const addMedToPlan = (med, action) => {
      const ok = appendPlanRows([{
        id: newMedPlanRowId(),
        action,
        medication_id: med.id || null,
        medication_name: med.medication_name || med.name || '',
        dosage: med.dosage || '',
        prescriber: med.prescriber || '',
        details: [med.frequency, med.purpose].filter(Boolean).join(' — '),
        reason: '',
        date: planSessionDate,
        client_id: clientId || med.client_id || null,
      }]);
      if (ok) {
        const label = action === 'continue' ? 'Continue' : action === 'discontinue' ? 'Discontinue' : 'Hold';
        toast.success(`${med.medication_name || med.name}: ${label} added to Medication Plan`);
      }
    };
    const openAddToPlan = () => {
      if (!planField) {
        toast.error('This template has no Medication Plan field. Add one in the template builder first.');
        return;
      }
      if (!setMedPlanCapture || !setShowMedicationForm) {
        toast.error('Add to Plan is not available in this editor.');
        return;
      }
      if (medicationClient && setSelectedClient) setSelectedClient(medicationClient);
      setMedPlanCapture(() => (fd) => {
        appendPlanRows([{
          id: newMedPlanRowId(),
          action: 'start',
          medication_id: null,
          medication_name: fd.medication_name || '',
          dosage: fd.dosage || '',
          prescriber: fd.prescriber || '',
          details: [fd.frequency, fd.purpose].filter(Boolean).join(' — '),
          reason: fd.notes || '',
          date: planSessionDate,
          client_id: clientId || null,
        }]);
        toast.success(`${fd.medication_name} added to Medication Plan (starts when the note is signed)`);
      });
      setShowMedicationForm(true);
    };
    
    return (
      <div key={index} style={{...fieldStyle, overflowWrap: 'break-word', maxWidth: '100%'}}>
        <Label>{field.label}{isSnapshot && <span className="text-xs text-gray-500 ml-2">(at time of note)</span>}</Label>
        {/* PHI-fix May 2026: warn if the saved snapshot looks like
            it was stamped from another patient's chart.  The corrupted
            snapshot is hidden — re-saving the note will heal it from
            this client's current medications via the auto-populate
            path in App.js. */}
        {snapshotLooksMismatched && (
          <SnapshotMismatchBanner
            noteId={selectedNote?.id}
            fieldId={field.id}
            fieldType="client_medications"
            expectedClientId={clientId}
            testId="medication-snapshot-mismatch-warning"
            message="The medication snapshot saved on this note belonged to a different patient and has been hidden. Save the note again to refresh the snapshot from this client's current medications."
          />
        )}
        <div className="border rounded p-2 bg-blue-50 border-blue-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-blue-200">
                <th className="text-left p-1 font-semibold text-blue-800">Medication</th>
                <th className="text-left p-1 font-semibold text-blue-800">Dosage</th>
                <th className="text-left p-1 font-semibold text-blue-800">Frequency</th>
                <th className="text-left p-1 font-semibold text-blue-800">Prescriber</th>
                <th className="text-left p-1 font-semibold text-blue-800">Purpose</th>
                {!isViewMode && <th className="text-left p-1 font-semibold text-blue-800 w-16">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {medicationsToShow && medicationsToShow.length > 0 ? (
                medicationsToShow.map((med, idx) => (
                  <tr key={idx} className="border-b border-blue-100 last:border-0">
                    <td className="p-1">
                      {med.medication_name || med.name}
                      {med.on_hold && (
                        <span className="text-amber-600 italic text-[10px] ml-1" title={med.hold_reason || ''}>(On Hold)</span>
                      )}
                    </td>
                    <td className="p-1">{med.dosage}</td>
                    <td className="p-1">{med.frequency}</td>
                    <td className="p-1">{med.prescriber || 'N/A'}</td>
                    <td className="p-1">{med.purpose || '-'}</td>
                    {!isViewMode && (
                      <td className="p-1">
                        <div className="flex gap-1 items-center">
                        {!isSnapshot && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            if (medicationClient && setSelectedClient && setEditingMedication && setShowMedicationForm) {
                              setSelectedClient(medicationClient);
                              setEditingMedication(med);
                              setShowMedicationForm(true);
                            }
                          }}
                          title="Edit medication"
                          disabled={disabled}
                          data-testid={`edit-medication-${med.id || idx}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        )}
                        {!isSnapshot && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={async () => {
                            if (!window.confirm(`Discontinue ${med.medication_name || med.name}?`)) return;
                            try {
                              // Get API from context or window
                              const apiBase = API || (typeof window !== 'undefined' && window.API) || '';
                              if (apiBase && med.id) {
                                const axios = (await import('axios')).default;
                                await axios.patch(`${apiBase}/api/medications/${med.id}/discontinue`);
                                // Trigger a re-fetch of medications if possible
                                window.location.reload();
                              }
                            } catch (error) {
                              console.error('Failed to discontinue medication:', error);
                              alert('Failed to discontinue medication');
                            }
                          }}
                          title="Discontinue medication"
                          disabled={disabled}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              disabled={disabled}
                              title="Medication plan actions"
                              data-testid={`med-plan-menu-${med.id || idx}`}
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem data-testid={`med-plan-continue-${med.id || idx}`} onClick={() => addMedToPlan(med, 'continue')}>
                              Continue
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid={`med-plan-discontinue-${med.id || idx}`} className="text-red-600 focus:text-red-700" onClick={() => addMedToPlan(med, 'discontinue')}>
                              Discontinue
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid={`med-plan-hold-${med.id || idx}`} className="text-amber-600 focus:text-amber-700" onClick={() => addMedToPlan(med, 'hold')}>
                              Hold
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid={`med-plan-add-${med.id || idx}`} onClick={openAddToPlan}>
                              Add to Plan…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr><td colSpan={!isViewMode ? "6" : "5"} className="p-1 text-center text-blue-600 italic">No medications on file</td></tr>
              )}
            </tbody>
          </table>
          {!isViewMode && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              title={isSnapshot ? (setMedSnapshotCapture ? "Adds the medication to the client's chart and to this note's medication list." : "Adds the medication to the client's chart. This note keeps its saved snapshot from the time of the note.") : undefined}
              onClick={() => {
                if (medicationClient && setSelectedClient && setShowMedicationForm) {
                  setSelectedClient(medicationClient);
                  if (isSnapshot && setMedSnapshotCapture && handleFieldChange) {
                    const fieldId = field.id;
                    const snapRaw = savedValue;
                    setMedSnapshotCapture(() => (created) => {
                      if (!created) return;
                      const med = { ...created, client_id: clientId || created.client_id || null };
                      handleFieldChange(prev => {
                        const raw = prev && prev[fieldId] !== undefined ? prev[fieldId] : snapRaw;
                        let arr = [];
                        if (typeof raw === 'string') { try { arr = JSON.parse(raw) || []; } catch { arr = []; } }
                        else if (Array.isArray(raw)) arr = raw;
                        return { ...(prev || {}), [fieldId]: JSON.stringify([...arr, med]) };
                      });
                      toast.success(`${med.medication_name || med.name || 'Medication'} added to this note's medication list`);
                    });
                  }
                  setShowMedicationForm(true);
                }
              }}
              disabled={disabled}
              data-testid={`note-add-medication-btn-${field.id}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Medication
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  // === MEDICATION PLAN (Jun 2026) ===
  // Rows populated by the med-list three-dot actions; fully editable by the
  // provider. Applied to the client's chart meds when the note is signed.
  if (field.type === 'medication_plan') {
    const rawPlanValue = newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : field.value;
    const planRows = parseMedPlanRows(rawPlanValue);
    const planDate = String(newNoteFormData?.date_of_service || newNoteFormData?.session_date || selectedNote?.date_of_service || selectedNote?.session_date || new Date().toISOString()).slice(0, 10);
    return (
      <div key={index} style={{ ...fieldStyle, overflowWrap: 'break-word', maxWidth: '100%' }}>
        <Label>{field.label || 'Medication Plan'}</Label>
        <MedicationPlanTable
          field={field}
          rows={planRows}
          sessionDate={planDate}
          isViewMode={isViewMode}
          disabled={disabled}
          onRowsChange={(next) => handleFieldChange(prev => ({ ...(prev || {}), [field.id]: JSON.stringify(next) }))}
        />
      </div>
    );
  }

  // === CLIENT ALLERGIES ===
  // "Frozen in time" - use saved snapshot from note if viewing/editing, live data only for new notes
  
  if (field.type === 'client_allergies') {
    // Check for saved snapshot data in the note (field.value)
    const savedValue = field.value || newNoteFormData[field.id];
    
    let allergiesText;
    let isSnapshot = false;
    
    if (savedValue) {
      // Use saved snapshot from note
      allergiesText = typeof savedValue === 'string' ? savedValue : JSON.stringify(savedValue);
      isSnapshot = true;
    } else {
      // No saved data - use live client allergies (for new note creation)
      // CRITICAL: Use noteClient (from selectedNoteClients), not selectedClient
      allergiesText = noteClient?.allergies || 'No known allergies';
    }
    
    return (
      <div key={index} style={{...fieldStyle, overflowWrap: 'break-word', maxWidth: '100%'}}>
        <Label>{field.label}{isSnapshot && <span className="text-xs text-gray-500 ml-2">(at time of note)</span>}</Label>
        <div className="border rounded p-2 bg-yellow-50 border-yellow-200">
          <p className="text-sm text-yellow-800">
            {allergiesText}
          </p>
        </div>
        {/* Jun 2026 — add a structured allergy to the chart straight from
            the note; the new entry is appended to this note's snapshot. */}
        {!isViewMode && (
          <AddAllergyInline
            clientId={noteClient?.id || selectedNoteClients?.[0] || selectedClient?.id}
            disabled={disabled}
            onSaved={(entry) => {
              if (!handleFieldChange || !entry) return;
              const seg = entry.allergen
                + ((entry.severity || entry.reaction)
                  ? ` (${[entry.severity, entry.reaction].filter(Boolean).join(' — ')})`
                  : '');
              const base = (allergiesText && allergiesText !== 'No known allergies') ? `${allergiesText}; ` : '';
              handleFieldChange(prev => ({ ...(prev || {}), [field.id]: base + seg }));
            }}
          />
        )}
      </div>
    );
  }
  
  // === CLIENT INSURANCE ===
  
  if (field.type === 'client_insurance') {
    // Check for saved snapshot first (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    let insurancesToShow = [];
    let isSnapshot = false;
    
    if (savedValue) {
      if (typeof savedValue === 'string') {
        try {
          insurancesToShow = JSON.parse(savedValue);
        } catch (e) {
          insurancesToShow = [];
        }
      } else if (Array.isArray(savedValue)) {
        insurancesToShow = savedValue;
      }
      isSnapshot = true;
    } else {
      // Get active insurances from the client (live data for new notes)
      // CRITICAL: Use noteClient (from selectedNoteClients), not selectedClient
      insurancesToShow = (noteClient?.insurances || []).filter(ins => ins.status === 'active');
    }
    
    return (
      <div key={index} style={{...fieldStyle, overflowWrap: 'break-word', maxWidth: '100%'}}>
        <Label>{field.label}{isSnapshot && <span className="text-xs text-gray-500 ml-2">(at time of note)</span>}</Label>
        <div className="border rounded p-2 bg-blue-50 border-blue-200">
          {insurancesToShow.length > 0 ? (
            insurancesToShow.map((ins, idx) => (
              <div key={idx} className="text-xs mb-2 last:mb-0">
                <div><strong className="text-blue-800">{ins.priority === 1 ? 'Primary' : ins.priority === 2 ? 'Secondary' : `Priority ${ins.priority}`}:</strong> {ins.company || ins.insurance_company}</div>
                {ins.plan && <div>Plan: {ins.plan}</div>}
                <div>Member ID: {ins.member_id || 'N/A'}</div>
                <div>Group: {ins.group_number || 'N/A'}</div>
              </div>
            ))
          ) : (
            <p className="text-xs text-blue-600 italic">Self Pay - No insurance on file</p>
          )}
        </div>
      </div>
    );
  }
  
  // === CLIENT VITALS (Most Recent) ===
  // "Frozen in time" - use saved snapshot from note if viewing/editing, live data only for new notes
  
  if (field.type === 'client_vitals') {
    const clientId = selectedNoteClients?.[0] || selectedClient?.id;

    // Check for saved snapshot data in the note (field.value) — "frozen
    // in time"; live most-recent chart vitals only for new notes.
    const savedValue = field.value || newNoteFormData[field.id];
    let vitalsToShow = null;
    let isSnapshot = false;
    let snapshotLooksMismatched = false;

    if (savedValue) {
      const parsed = parseVitalsSnapshot(savedValue);
      // PHI-fix May 2026: check the snapshot's own ``client_id`` stamp
      // (when present) against the note's client. Legacy snapshots
      // without the stamp still render (backwards compat).
      if (clientId && parsed && parsed.client_id && parsed.client_id !== clientId) {
        snapshotLooksMismatched = true;
        vitalsToShow = null;
      } else {
        vitalsToShow = parsed;
      }
      isSnapshot = !snapshotLooksMismatched;
    } else {
      // No saved data — use live client vitals (for new note creation),
      // PHI-scoped by client_id (mirrors the medication snapshot fix).
      if (clientVitals && clientVitals.length > 0) {
        const scoped = clientId
          ? clientVitals.filter(v => v.client_id === clientId)
          : clientVitals;
        if (scoped.length > 0) {
          const sortedVitals = [...scoped].sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));
          vitalsToShow = sortedVitals[0];
        }
      }
    }

    return (
      <div key={index} style={{ ...fieldStyle, overflowWrap: 'break-word', maxWidth: '100%' }}>
        <Label>{field.label || 'Most Recent Vitals'}{isSnapshot && <span className="text-xs text-gray-500 ml-2">(at time of note)</span>}</Label>
        {snapshotLooksMismatched && (
          <SnapshotMismatchBanner
            noteId={selectedNote?.id}
            fieldId={field.id}
            fieldType="client_vitals"
            expectedClientId={clientId}
            testId="vitals-snapshot-mismatch-warning"
            message="The vitals snapshot saved on this note belonged to a different patient and has been hidden. Save the note again to refresh the snapshot from this client's current vitals."
          />
        )}
        <MostRecentVitalsCard vitals={vitalsToShow} />
        {!isViewMode && !disabled && clientId && (
          <AddVitalsDialog
            clientId={clientId}
            onSaved={(reading) => {
              if (handleFieldChange) {
                handleFieldChange(prev => ({ ...(prev || {}), [field.id]: JSON.stringify(reading) }));
              }
            }}
          />
        )}
      </div>
    );
  }
  
  // === CLIENT DIAGNOSES (read-only display) ===
  // "Frozen in time" - use saved snapshot from note if viewing/editing, live data only for new notes
  
  if (field.type === 'client_diagnoses') {
    // Get client ID from selectedNoteClients or fall back to selectedClient
    const clientId = selectedNoteClients?.[0] || selectedClient?.id;
    const diagnosesClient = clients.find(c => c.id === clientId);
    
    // Check for saved snapshot data in the note (field.value)
    const savedValue = field.value || newNoteFormData[field.id];
    let diagnosesToShow = [];
    let isSnapshot = false;
    let snapshotLooksMismatched = false;
    
    if (savedValue) {
      // Use saved snapshot from note
      let parsed = [];
      if (typeof savedValue === 'string') {
        try {
          parsed = JSON.parse(savedValue);
        } catch (e) {
          parsed = [];
        }
      } else if (Array.isArray(savedValue)) {
        parsed = savedValue;
      }
      
      // PHI-fix May 2026 — same defense-in-depth as the medication
      // snapshot renderer.  If every stamped row in the saved
      // snapshot has a ``client_id`` that differs from the note's
      // client we've got a corrupted cross-patient snapshot and we
      // refuse to render it.  Legacy snapshots without ``client_id``
      // stamps still render (backwards compat).
      if (clientId && Array.isArray(parsed) && parsed.length > 0) {
        const stamped = parsed.filter(d => d && d.client_id);
        if (stamped.length > 0 && stamped.every(d => d.client_id !== clientId)) {
          snapshotLooksMismatched = true;
          diagnosesToShow = [];
        } else {
          diagnosesToShow = parsed;
        }
      } else {
        diagnosesToShow = parsed;
      }
      isSnapshot = !snapshotLooksMismatched;
    } else {
      // No saved data - use live client diagnoses (for new note creation)
      diagnosesToShow = clientDiagnoses?.filter(d => d.client_id === clientId && d.is_active !== false) || [];
    }
    
    const getDiagUserName = (userId, cachedName) => {
      if (cachedName) return cachedName;
      const foundUser = users.find(u => u.id === userId);
      return foundUser ? (foundUser.full_name || `${foundUser.first_name || ''} ${foundUser.last_name || ''}`.trim()) : 'Unknown';
    };
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}{isSnapshot && <span className="text-xs text-gray-500 ml-2">(at time of note)</span>}</Label>
        {/* PHI-fix May 2026: same defense-in-depth warning as the
            medication renderer.  Hidden corrupted snapshot is dropped;
            user re-saves to heal via the auto-populate path. */}
        {snapshotLooksMismatched && (
          <SnapshotMismatchBanner
            noteId={selectedNote?.id}
            fieldId={field.id}
            fieldType="client_diagnoses"
            expectedClientId={clientId}
            testId="diagnosis-snapshot-mismatch-warning"
            message="The diagnosis snapshot saved on this note belonged to a different patient and has been hidden. Save the note again to refresh the snapshot from this client's current diagnoses."
          />
        )}
        <div className="border rounded p-2 bg-blue-50">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-100">
                <th className="text-left p-1">Tags</th>
                <th className="text-left p-1">Code</th>
                <th className="text-left p-1">Description</th>
                <th className="text-left p-1">Date</th>
                <th className="text-left p-1">Provider(s)</th>
              </tr>
            </thead>
            <tbody>
              {diagnosesToShow.length > 0 ? (
                diagnosesToShow.map((diag, idx) => (
                  <tr key={idx}>
                    <td className="p-1">
                      <div className="flex flex-wrap gap-1">
                        {diag.is_primary && (
                          <span className="text-xs bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded">Primary</span>
                        )}
                        {diag.diagnosis_tag && (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            diag.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                            diag.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                            diag.diagnosis_tag === 'by_history' ? 'bg-blue-100 text-blue-700' :
                            diag.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {diag.diagnosis_tag === 'differential' ? 'Differential' : 
                             diag.diagnosis_tag === 'working' ? 'Working' : 
                             diag.diagnosis_tag === 'by_history' ? 'By History' :
                             diag.diagnosis_tag === 'provisional' ? 'Provisional' :
                             diag.diagnosis_tag}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-1">{diag.code || diag.diagnosis_code}</td>
                    <td className="p-1">{diag.description || diag.diagnosis_description}</td>
                    <td className="p-1">{diag.start_date ? new Date(diag.start_date).toLocaleDateString() : 'N/A'}</td>
                    <td className="p-1">{diag.provider_names?.length > 0 ? diag.provider_names.join(', ') : getDiagUserName(diag.assigned_by, diag.assigned_by_name)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" className="p-1 text-center text-gray-500">No diagnoses on file</td></tr>
              )}
            </tbody>
          </table>
          {!isViewMode && !isSnapshot && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={() => {
                if (diagnosesClient && setSelectedClient && setShowDiagnosisForm) {
                  setSelectedClient(diagnosesClient);
                  setShowDiagnosisForm(true);
                }
              }}
              disabled={disabled}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Diagnosis
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  // === CLIENT DIAGNOSES SELECTOR (interactive) ===
  
  if (field.type === 'client_diagnoses_selector') {
    // Get client ID from selectedNoteClients or fall back to selectedClient
    const clientId = selectedNoteClients?.[0] || selectedClient?.id;
    const rawDiagnoses = clientDiagnoses?.filter(d => d.client_id === clientId && d.is_active) || [];
    // May 2026 defense — skip rows where BOTH code/diagnosis_code AND
    // description/diagnosis_description are missing.  Without this an
    // in-flight bundle merge can briefly render empty checkbox rows
    // (the user-reported "checkboxes are there but I can't see the
    // diagnoses" screenshot) instead of just showing "No active
    // diagnoses on file" until real data lands.
    const availableDiagnoses = rawDiagnoses.filter(d => {
      const code = d.code || d.diagnosis_code;
      const desc = d.description || d.diagnosis_description;
      return code || desc;
    });
    
    // Parse saved diagnosis data - can be in multiple formats:
    // 1. JSON string of diagnosis objects (saved note)
    // 2. Array of diagnosis objects (parsed)
    // 3. Array of diagnosis IDs (during edit)
    let selectedDiagnoses = [];
    let selectedDiagnosisIds = [];
    
    // Check field.value first (saved note data)
    const savedValue = field.value || newNoteFormData[field.id];
    
    if (savedValue) {
      if (typeof savedValue === 'string' && savedValue.startsWith('[')) {
        // JSON string of diagnosis objects
        try {
          const parsed = JSON.parse(savedValue);
          if (Array.isArray(parsed)) {
            if (parsed.length > 0 && typeof parsed[0] === 'object') {
              // Array of diagnosis objects
              selectedDiagnoses = parsed;
              selectedDiagnosisIds = parsed.map(d => d.id);
            } else if (parsed.length > 0 && typeof parsed[0] === 'string') {
              // Array of IDs
              selectedDiagnosisIds = parsed;
            }
          }
        } catch (e) {
          console.warn('Failed to parse diagnosis selector value:', e);
        }
      } else if (Array.isArray(savedValue)) {
        if (savedValue.length > 0 && typeof savedValue[0] === 'object') {
          // Already parsed array of diagnosis objects
          selectedDiagnoses = savedValue;
          selectedDiagnosisIds = savedValue.map(d => d.id);
        } else {
          // Array of IDs
          selectedDiagnosisIds = savedValue;
        }
      }
    }
    
    // View mode - show selected diagnoses as pills with tags
    if (isViewMode) {
      // Use saved diagnoses objects if available, otherwise look up by IDs
      const diagnosesToShow = selectedDiagnoses.length > 0 
        ? selectedDiagnoses 
        : availableDiagnoses.filter(d => selectedDiagnosisIds.includes(d.id));
      
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {diagnosesToShow.length > 0 ? (
              diagnosesToShow.map((dx, idx) => (
                <div key={dx.id || idx} className="flex items-center gap-1">
                  {dx.is_primary && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-1 py-0.5 rounded">Primary</span>
                  )}
                  {dx.diagnosis_tag && (
                    <span className={`text-xs px-1 py-0.5 rounded font-medium ${
                      dx.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                      dx.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                      dx.diagnosis_tag === 'by_history' ? 'bg-blue-100 text-blue-700' :
                      dx.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {dx.diagnosis_tag === 'differential' ? 'Diff' : 
                       dx.diagnosis_tag === 'working' ? 'Working' : 
                       dx.diagnosis_tag === 'by_history' ? 'Hx' :
                       dx.diagnosis_tag === 'provisional' ? 'Prov' :
                       dx.diagnosis_tag}
                    </span>
                  )}
                  <ViewPill>
                    <code className="font-mono text-xs mr-1">{dx.code || dx.diagnosis_code}</code>
                    {dx.description || dx.diagnosis_description}
                  </ViewPill>
                </div>
              ))
            ) : (
              <span className="text-gray-400 italic text-sm">No diagnoses selected</span>
            )}
          </div>
        </div>
      );
    }
    
    // Get client for Add Diagnosis handler
    const diagnosesClient = clients.find(c => c.id === clientId);
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        <div className="border rounded p-3 bg-teal-50">
          <p className="text-xs text-gray-600 mb-2">Select diagnoses to include in this note:</p>
          <div className="border rounded bg-white p-2 max-h-48 overflow-y-auto">
            {availableDiagnoses.length > 0 ? (
              availableDiagnoses.map((dx) => (
                <label key={dx.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 cursor-pointer rounded">
                  <input
                    type="checkbox"
                    checked={selectedDiagnosisIds.includes(dx.id)}
                    onChange={(e) => {
                      const current = selectedDiagnosisIds;
                      if (e.target.checked) {
                        onChange([...current, dx.id]);
                      } else {
                        onChange(current.filter(id => id !== dx.id));
                      }
                    }}
                    disabled={disabled}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm flex-1 flex items-center gap-1 flex-wrap">
                    {/* PHI-fix May 2026: DB stores diagnosis_code /
                        diagnosis_description (canonical schema from
                        the client_diagnoses collection).  The
                        auto-populate path in App.js already accepts
                        BOTH naming conventions; this renderer was
                        only reading ``dx.code`` / ``dx.description``
                        and rendering blank labels (user-visible bug
                        — checkbox + Primary tag rendered, but the
                        ICD code + descriptor were missing). */}
                    <code className="font-mono text-xs bg-gray-100 px-1 rounded">{dx.code || dx.diagnosis_code}</code>
                    <span>{dx.description || dx.diagnosis_description}</span>
                    {dx.is_primary && <span className="text-xs bg-teal-100 text-teal-700 px-1 rounded">Primary</span>}
                    {dx.diagnosis_tag && (
                      <span className={`text-xs px-1 rounded font-medium ${
                        dx.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                        dx.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                        dx.diagnosis_tag === 'by_history' ? 'bg-blue-100 text-blue-700' :
                        dx.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {dx.diagnosis_tag === 'differential' ? 'Differential' : 
                         dx.diagnosis_tag === 'working' ? 'Working' : 
                         dx.diagnosis_tag === 'by_history' ? 'By History' :
                         dx.diagnosis_tag === 'provisional' ? 'Provisional' :
                         dx.diagnosis_tag}
                      </span>
                    )}
                  </span>
                </label>
              ))
            ) : (
              <p className="text-sm text-gray-400 italic py-2">No active diagnoses on file</p>
            )}
          </div>
          {selectedDiagnosisIds.length > 0 && (
            <p className="text-xs text-teal-600 mt-2">{selectedDiagnosisIds.length} diagnosis(es) selected</p>
          )}
          {/* Add New Diagnosis Button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 border-teal-300 text-teal-700 hover:bg-teal-50"
            onClick={() => {
              if (diagnosesClient && setSelectedClient && setShowDiagnosisForm) {
                setSelectedClient(diagnosesClient);
                setShowDiagnosisForm(true);
              }
            }}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add New Diagnosis
          </Button>
        </div>
      </div>
    );
  }
  
  // === TREATMENT PLAN GOALS ===
  
  if (field.type === 'treatment_plan_goals') {
    return (
      <div key={index} style={{...fieldStyle, width: '100%'}}>
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-5 w-5 text-purple-600" />
            <div>
              <Label className="font-medium text-purple-900">Treatment Plan Goals</Label>
              {linkedGoals.length > 0 && linkedGoals[0].provider_name && (
                <p className="text-xs text-purple-600 italic">(developed by {linkedGoals[0].provider_name})</p>
              )}
            </div>
            {linkedGoals.length > 0 && (
              <span className="bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full text-xs font-medium">
                {linkedGoals.length} linked
              </span>
            )}
            {!isViewMode && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="ml-auto border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={() => {
                  // Call fetchTreatmentPlanGoals if available with client ID
                  const clientId = selectedNoteClients?.[0] || selectedClient?.id;
                  if (fetchTreatmentPlanGoals && clientId) {
                    fetchTreatmentPlanGoals(clientId);
                  }
                  // Open the dialog
                  if (setShowLinkGoalsDialog) {
                    setShowLinkGoalsDialog(true);
                  }
                }}
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-1" />
                Link Goals
              </Button>
            )}
          </div>
          {linkedGoals.length > 0 ? (
            <div className="space-y-2">
              {linkedGoals.map((goal, gIdx) => (
                <div key={goal.goal_id || gIdx} className="bg-white rounded border border-purple-200 p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-1">
                      {goal.provider_name && (
                        <div className="text-xs text-purple-600 mb-1">
                          <span className="font-medium">Developed by:</span> {goal.provider_name}
                        </div>
                      )}
                      {goal.pan_name && (
                        <div className="text-sm">
                          <span className="font-semibold text-purple-800">PAN:</span>{' '}
                          <span className="text-gray-800">{goal.pan_name}</span>
                        </div>
                      )}
                      <div className="text-sm">
                        <span className="font-semibold text-purple-800">Goal:</span>{' '}
                        <span className="text-gray-800">{goal.goal_text}</span>
                      </div>
                      {goal.goal_in_clients_words && (
                        <div className="text-sm">
                          <span className="font-semibold text-purple-800">Goal in Client's Words:</span>{' '}
                          <span className="text-gray-800 italic">"{goal.goal_in_clients_words}"</span>
                        </div>
                      )}
                      {goal.objectives && goal.objectives.length > 0 && (
                        <div className="text-sm">
                          <span className="font-semibold text-purple-800">Objectives:</span>
                          <div className="mt-1 pl-3 border-l-2 border-purple-200 space-y-1">
                            {goal.objectives.map((obj, objIdx) => (
                              <div key={obj.objective_id || objIdx} className="text-xs text-gray-600">
                                <span className="font-medium">{objIdx + 1}.</span> {obj.objective_text}
                                {obj.intervention && (
                                  <span className="text-gray-400 ml-1">• {obj.intervention}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {goal.service_type && (
                        <div className="mt-2">
                          <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                            {goal.service_type}
                          </span>
                        </div>
                      )}
                    </div>
                    {!isViewMode && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                        onClick={() => setLinkedGoals && setLinkedGoals(prev => prev.filter(g => g.goal_id !== goal.goal_id))}
                        disabled={disabled}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-purple-700 italic">
              {isViewMode ? 'No goals linked to this note.' : 'Click "Link Goals" to pull in goals from active treatment plans.'}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // === CONSENT AFFIRMATION ===
  
  if (field.type === 'consent_affirmation') {
    const {
      brandingSettings = {},
      organizations = [],
    } = context;
    
    // Define rawState at the parent scope so all helper functions can access it
    const rawState = brandingSettings?.company_state || organizations?.[0]?.state || 'Massachusetts';
    const formattedState = formatStateForConsent(rawState);
    
    // Get location name
    const getProviderLocationText = () => {
      if (telehealthConsent?.provider_location === 'home') {
        return ' I am providing this session from my private home.';
      } else if (telehealthConsent?.provider_location) {
        const loc = locations.find(l => l.id === telehealthConsent.provider_location);
        return ` I am providing this session from ${loc?.name || 'my office'}.`;
      }
      return '';
    };
    
    // Get POS-specific text
    const getPOSText = () => {
      if (telehealthConsent?.pos_code === '10') {
        return ` The client has confirmed that they are located in their private home in ${formattedState}.`;
      } else if (telehealthConsent?.pos_code === '02') {
        return ` The client has confirmed that they are located at a location other than their home in ${formattedState}.`;
      } else if (telehealthConsent?.pos_code === '03') {
        return ` The client has confirmed that they are located at their school in ${formattedState}.`;
      }
      return '';
    };
    
    // Build consent language based on modality
    const getConsentLanguage = () => {
      const modality = telehealthConsent?.modality;
      const posDesc = telehealthConsent?.pos_description || 
        (organizations?.[0]?.pos_codes || posCodes || []).find(p => p.code === telehealthConsent?.pos_code)?.description ||
        'the designated location';
      
      if (modality === 'in_person') {
        return `This session was conducted in-person at ${posDesc} in ${formattedState}.${getProviderLocationText()}`;
      } else if (modality === 'audio_visual') {
        return `I attest that the client has given verbal consent to participate in this telehealth session via video.${getPOSText()}${getProviderLocationText()}`;
      } else if (modality === 'audio_only') {
        return `I attest that the client has given verbal consent to participate in this telehealth session via phone. The client has declined or was unable to participate via audio/visual (video) session.${getPOSText()}${getProviderLocationText()}`;
      }
      return '';
    };
    
    // View mode - show summary of consent with full language
    if (isViewMode) {
      const locationName = locations.find(l => l.id === telehealthConsent?.provider_location)?.name || 
        (telehealthConsent?.provider_location === 'home' ? 'Provider\'s Home' : 'Not specified');
      const posDesc = telehealthConsent?.pos_description || 
        (organizations?.[0]?.pos_codes || posCodes || []).find(p => p.code === telehealthConsent?.pos_code)?.description || '';
      const modalityText = telehealthConsent?.modality === 'in_person' ? 'In-Person' 
        : telehealthConsent?.modality === 'audio_visual' ? 'Audio/Visual (Video)'
        : telehealthConsent?.modality === 'audio_only' ? 'Audio Only (Phone)'
        : 'Not specified';
      
      const consentLanguage = getConsentLanguage();
      
      return (
        <div key={index} style={{...fieldStyle, width: '100%'}}>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Building className="h-5 w-5 text-blue-600" />
              <Label className="font-medium text-blue-900">Consent Affirmation</Label>
              {telehealthConsent?.pos_code && telehealthConsent?.modality && (
                <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                  ✓ Confirmed
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <span className="text-xs text-gray-500 block">Provider Location</span>
                <span className="text-sm font-medium">{locationName}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Place of Service</span>
                <span className="text-sm font-medium">{telehealthConsent?.pos_code ? `${telehealthConsent.pos_code} - ${posDesc}` : 'Not selected'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Session Modality</span>
                <span className="text-sm font-medium">{modalityText}</span>
              </div>
            </div>
            {/* Full Consent Language */}
            {consentLanguage && (
              <div className={`border rounded-lg p-3 ${
                telehealthConsent?.modality === 'in_person' ? 'bg-green-50 border-green-200' 
                : telehealthConsent?.modality === 'audio_visual' ? 'bg-blue-100 border-blue-300'
                : 'bg-purple-50 border-purple-200'
              }`}>
                <p className="text-sm text-gray-700">{consentLanguage}</p>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div key={index} style={{...fieldStyle, width: '100%'}}>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building className="h-5 w-5 text-blue-600" />
            <Label className="font-medium text-blue-900">Consent Affirmation</Label>
            {telehealthConsent?.pos_code && telehealthConsent?.modality && (
              <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                ✓ Confirmed
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-sm text-gray-700 mb-1 block">Provider Location</Label>
              <Select
                value={telehealthConsent?.provider_location || ''}
                onValueChange={(value) => setTelehealthConsent && setTelehealthConsent(prev => ({
                  ...prev,
                  provider_location: value
                }))}
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select location..." />
                </SelectTrigger>
                <SelectContent>
                  {(locations || []).map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                  <SelectItem value="home">Provider's Home</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-700 mb-1 block">Place of Service</Label>
              <Select
                value={telehealthConsent?.pos_code || ''}
                onValueChange={(value) => {
                  const posObj = (organizations?.[0]?.pos_codes || posCodes || []).find(p => p.code === value);
                  setTelehealthConsent && setTelehealthConsent(prev => ({
                    ...prev,
                    pos_code: value,
                    pos_description: posObj?.description || ''
                  }));
                }}
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select POS..." />
                </SelectTrigger>
                <SelectContent>
                  {(organizations?.[0]?.pos_codes || posCodes || []).map(pos => (
                    <SelectItem key={pos.code} value={pos.code}>{pos.code} - {pos.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-700 mb-1 block">Session Modality</Label>
              <Select
                value={telehealthConsent?.modality || ''}
                onValueChange={(value) => setTelehealthConsent && setTelehealthConsent(prev => ({
                  ...prev,
                  modality: value,
                  // Keep derived keys consistent so a stale modifier can never contradict modality
                  modifier: value === 'audio_only' ? '93' : value === 'audio_visual' ? '95' : null,
                  session_modality: value === 'audio_only' ? 'phone' : value === 'audio_visual' ? 'video' : 'in_person'
                }))}
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select modality..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In-Person</SelectItem>
                  <SelectItem value="audio_visual">Audio/Visual (Video)</SelectItem>
                  <SelectItem value="audio_only">Audio Only (Phone)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {telehealthConsent?.pos_code && telehealthConsent?.modality && (
            <div className={`border rounded-lg p-3 ${
              telehealthConsent.modality === 'in_person' ? 'bg-green-50 border-green-200' 
              : telehealthConsent.modality === 'audio_visual' ? 'bg-blue-50 border-blue-200'
              : 'bg-purple-50 border-purple-200'
            }`}>
              <p className="text-sm text-gray-700">
                {telehealthConsent.modality === 'in_person' ? (
                  <>This session was conducted in-person at {telehealthConsent.pos_description || 'the designated location'} in {formattedState}.</>
                ) : telehealthConsent.modality === 'audio_visual' ? (
                  <>I attest that the client has given verbal consent to participate in this telehealth session via video.
                  {telehealthConsent.pos_code === '10' && <> The client has confirmed that they are located in their private home in {formattedState}.</>}
                  {telehealthConsent.pos_code === '02' && <> The client has confirmed that they are located at a location other than their home in {formattedState}.</>}
                  {telehealthConsent.pos_code === '03' && <> The client has confirmed that they are located at their school in {formattedState}.</>}
                  {telehealthConsent.provider_location === 'home' 
                    ? <> I am providing this session from my private home.</>
                    : telehealthConsent.provider_location 
                      ? <> I am providing this session from {locations.find(l => l.id === telehealthConsent.provider_location)?.name || 'my office'}.</>
                      : null}</>
                ) : (
                  <>I attest that the client has given verbal consent to participate in this telehealth session via phone. The client has declined or was unable to participate via audio/visual (video) session.
                  {telehealthConsent.pos_code === '10' && <> The client has confirmed that they are located in their private home in {formattedState}.</>}
                  {telehealthConsent.pos_code === '02' && <> The client has confirmed that they are located at a location other than their home in {formattedState}.</>}
                  {telehealthConsent.pos_code === '03' && <> The client has confirmed that they are located at their school in {formattedState}.</>}
                  {telehealthConsent.provider_location === 'home' 
                    ? <> I am providing this session from my private home.</>
                    : telehealthConsent.provider_location 
                      ? <> I am providing this session from {locations.find(l => l.id === telehealthConsent.provider_location)?.name || 'my office'}.</>
                      : null}</>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // === DROPDOWN FIELDS ===
  
  // Service dropdown - multiselect with checkboxes
  if (field.type === 'service_dropdown') {
    const currentValues = Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : [];
    const safeServiceTypes = Array.isArray(serviceTypes) ? serviceTypes : [];
    
    // View mode - show selected services as pills
    if (isViewMode) {
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {currentValues.length > 0 ? (
              currentValues.map((val, idx) => {
                const service = safeServiceTypes.find(st => st.id === val);
                return (
                  <ViewPill key={idx} variant="selected">
                    {service?.display_name || service?.name || val}
                  </ViewPill>
                );
              })
            ) : (
              <span className="text-gray-400 italic text-sm">No services selected</span>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <div 
              className={`border rounded-md p-2 min-h-[38px] bg-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
              data-testid="service-dropdown-multiselect"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {currentValues.length > 0 ? (
                    currentValues.map((val, idx) => {
                      const service = safeServiceTypes.find(st => st.id === val);
                      return (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {service?.display_name || service?.name || val}
                          <button
                            type="button"
                            className="ml-1 hover:text-blue-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newValues = currentValues.filter(v => v !== val);
                              onChange(newValues);
                            }}
                            disabled={disabled}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-gray-400 text-sm">
                      {safeServiceTypes.length === 0 ? 'Loading services...' : 'Select services...'}
                    </span>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2 max-h-60 overflow-y-auto" align="start">
            {safeServiceTypes.filter(st => st.is_active !== false).length > 0 ? (
              safeServiceTypes.filter(st => st.is_active !== false).map((service) => (
                <div key={service.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  onClick={() => {
                    const newValues = currentValues.includes(service.id)
                      ? currentValues.filter(v => v !== service.id)
                      : [...currentValues, service.id];
                    onChange(newValues);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={currentValues.includes(service.id)}
                    onChange={() => {}}
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <span className="flex-1 text-sm">
                    {service.display_name || service.name}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-gray-400 text-sm p-2">
                {safeServiceTypes.length === 0 
                  ? 'No services loaded. Please refresh the page or contact support.' 
                  : 'No active services available'}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  }
  
  if (field.type === 'location_dropdown') {
    // View mode
    if (isViewMode) {
      const location = locations.find(l => l.id === value);
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          {location ? (
            <ViewPill>{location.name}</ViewPill>
          ) : (
            <ViewValue />
          )}
        </div>
      );
    }
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Select 
          value={value} 
          onValueChange={(val) => onChange(val)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select location..." />
          </SelectTrigger>
          <SelectContent>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  if (field.type === 'provider_dropdown') {
    // Check for saved value (frozen in time)
    const savedValue = field.value || newNoteFormData[field.id];
    
    // View mode - show saved provider name or look up from providers
    if (isViewMode) {
      let displayName = 'No provider selected';
      if (savedValue) {
        // If saved value is a name string, use it; otherwise look up by ID
        if (typeof savedValue === 'string' && !savedValue.includes('-')) {
          displayName = savedValue;
        } else {
          const provider = providers.find(p => p.id === savedValue);
          displayName = provider?.full_name || savedValue;
        }
      } else if (value) {
        const provider = providers.find(p => p.id === value);
        displayName = provider?.full_name || 'Unknown provider';
      }
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          {displayName !== 'No provider selected' ? (
            <ViewPill>{displayName}</ViewPill>
          ) : (
            <ViewValue>{displayName}</ViewValue>
          )}
        </div>
      );
    }
    
    // Edit mode - show dropdown with client's assigned providers first, then all providers
    const clientProviderIds = selectedClient?.assigned_providers || [];
    const clientProviders = providers.filter(p => clientProviderIds.includes(p.id));
    const otherProviders = providers.filter(p => !clientProviderIds.includes(p.id));
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Select 
          value={value} 
          onValueChange={(val) => onChange(val)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select provider..." />
          </SelectTrigger>
          <SelectContent>
            {clientProviders.length > 0 && (
              <>
                <SelectItem value="_header_assigned" disabled className="text-xs text-gray-500 font-semibold">
                  — Client's Providers —
                </SelectItem>
                {clientProviders.map((prov) => (
                  <SelectItem key={prov.id} value={prov.id}>
                    {prov.full_name}
                  </SelectItem>
                ))}
                {otherProviders.length > 0 && (
                  <SelectItem value="_header_other" disabled className="text-xs text-gray-500 font-semibold">
                    — Other Providers —
                  </SelectItem>
                )}
              </>
            )}
            {otherProviders.map((prov) => (
              <SelectItem key={prov.id} value={prov.id}>
                {prov.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  if (field.type === 'group_dropdown') {
    // Filter for active groups only
    const activeGroups = (allGroups || []).filter(g => g.status === 'active' || !g.status);
    
    // View mode
    if (isViewMode) {
      const group = allGroups.find(g => g.id === value);
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          {group ? (
            <ViewPill>{group.name}</ViewPill>
          ) : (
            <ViewValue>No group selected</ViewValue>
          )}
        </div>
      );
    }
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Select 
          value={value} 
          onValueChange={(val) => onChange(val)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select group..." />
          </SelectTrigger>
          <SelectContent>
            {activeGroups.length > 0 ? (
              activeGroups.map((grp) => (
                <SelectItem key={grp.id} value={grp.id}>
                  {grp.name}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="_none" disabled>No active groups available</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  // === GROUP FIELDS ===
  
  if (field.type === 'group_attendance' && isGroupNote) {
    // View mode - show attendance summary
    if (isViewMode) {
      const presentCount = Object.values(groupAttendance).filter(v => v === 'present').length;
      const absentCount = Object.values(groupAttendance).filter(v => v === 'absent').length;
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-sm">
              <span className="text-green-600 font-medium">{presentCount} Present</span>
              {absentCount > 0 && <span className="text-red-600 font-medium ml-3">{absentCount} Absent</span>}
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        <div className="border rounded p-3 bg-orange-50">
          <p className="text-sm text-orange-700">Group Attendance Tracker</p>
          <p className="text-xs text-gray-500 mt-1">
            {groupMembers.length > 0 
              ? `${groupMembers.length} members in group` 
              : 'No group members loaded'}
          </p>
        </div>
      </div>
    );
  }
  
  if (field.type === 'group_member_notes' && isGroupNote) {
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        <div className="border rounded p-3 bg-orange-50">
          <p className="text-sm text-orange-700">Per-Member Notes</p>
          <p className="text-xs text-gray-500 mt-1">
            Individual notes for each group member
          </p>
        </div>
      </div>
    );
  }
  
  // === MATRIX FIELD ===
  
  if (field.type === 'matrix') {
    const matrixData = newNoteFormData[field.id] || {};
    const extraRows = matrixData._extraRows || [];
    const baseRows = matrixBaseRows(field);
    const allRows = [...baseRows, ...extraRows];
    const columnHeaders = field.columnHeaders || [];
    
    // View mode - render as styled read-only table
    if (isViewMode) {
      const cellHasData = (v) => v !== undefined && v !== null && v !== '' && v !== false && v !== 'false';
      const rowHasData = (rowIdx) => columnHeaders.some((_, colIdx) => cellHasData(matrixCellValue(field, matrixData, rowIdx, colIdx)));
      // Narrow columns (checkbox/dropdown) shrink to content so wide text columns get the space
      const isNarrowCol = (colIdx) => {
        const ct = field.columnTypes?.[colIdx] || field.cellType || 'text';
        return ct === 'checkbox' || ct === 'dropdown';
      };
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          <div className="border rounded overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {!field.hide_row_labels && <th className="border px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap w-px"></th>}
                  {columnHeaders.map((header, colIdx) => (
                    <th key={colIdx} className={`border px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap ${isNarrowCol(colIdx) ? 'w-px' : ''}`}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allRows.map((row, rowIdx) => {
                  if (!rowHasData(rowIdx)) {
                    return (
                      <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {!field.hide_row_labels && <td className="border px-3 py-1.5 font-medium text-gray-400 whitespace-nowrap text-xs">{row}</td>}
                        <td colSpan={columnHeaders.length} className="border px-3 py-1.5 text-xs text-gray-400 italic">No data</td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {!field.hide_row_labels && <td className="border px-3 py-2 font-medium text-gray-800 whitespace-nowrap align-top">{row}</td>}
                      {columnHeaders.map((col, colIdx) => {
                        const cellKey = `${rowIdx}-${colIdx}`;
                        const cellValue = matrixCellValue(field, matrixData, rowIdx, colIdx);
                        const columnType = field.columnTypes?.[colIdx] || field.cellType || 'text';
                        
                        return (
                          <td key={colIdx} className={`border px-3 py-2 text-gray-700 align-top ${isNarrowCol(colIdx) ? 'whitespace-nowrap text-center' : ''}`}>
                            {columnType === 'checkbox' ? (
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                                cellValue === 'true' || cellValue === true 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-gray-200'
                              }`}>
                                {(cellValue === 'true' || cellValue === true) && '✓'}
                              </span>
                            ) : columnType === 'dropdown' && cellValue ? (
                              <span className="inline-block whitespace-nowrap"><ViewPill>{cellValue}</ViewPill></span>
                            ) : (
                              <span className="whitespace-pre-wrap">{cellValue || '-'}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {allRows.length === 0 && (
                  <tr>
                    <td colSpan={columnHeaders.length + 1} className="border p-2 text-center text-gray-400 italic">
                      No data entered
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    
    // Edit mode - full interactive table
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <div className="space-y-2">
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  {!field.hide_row_labels && <th className="border p-2 text-left font-semibold whitespace-nowrap w-px"></th>}
                  {columnHeaders.map((header, colIdx) => {
                    const ct = field.columnTypes?.[colIdx] || field.cellType || 'text';
                    return (
                      <th key={colIdx} className={`border p-2 text-left font-semibold ${ct === 'checkbox' ? 'w-px whitespace-nowrap text-center' : ''}`}>{header}</th>
                    );
                  })}
                  {extraRows.length > 0 && <th className="border p-2 w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {allRows.map((row, rowIdx) => {
                  const isExtraRow = rowIdx >= baseRows.length;
                  return (
                    <tr key={rowIdx}>
                      {!field.hide_row_labels && (
                      <td className="border p-2 font-medium bg-gray-50 whitespace-nowrap w-px align-top">
                        {isExtraRow ? (
                          <DebouncedInput 
                            className="h-7 text-xs font-medium min-w-[10rem]"
                            value={row}
                            onChange={(e) => {
                              const newExtraRows = [...extraRows];
                              newExtraRows[rowIdx - baseRows.length] = e.target.value;
                              const updatedMatrix = { ...matrixData, _extraRows: newExtraRows };
                              onChange(updatedMatrix);
                            }}
                            placeholder="Row label"
                            disabled={disabled}
                            debounceMs={200}
                          />
                        ) : row}
                      </td>
                      )}
                      {columnHeaders.map((col, colIdx) => {
                        const cellKey = `${rowIdx}-${colIdx}`;
                        const cellValue = matrixCellValue(field, matrixData, rowIdx, colIdx) || '';
                        const columnType = field.columnTypes?.[colIdx] || field.cellType || 'text';
                        
                        return (
                          <td key={colIdx} className={`border p-1 ${columnType === 'checkbox' ? 'w-px whitespace-nowrap' : ''}`}>
                            {columnType === 'dropdown' ? (
                              <Select 
                                value={cellValue}
                                onValueChange={(value) => {
                                  const updatedMatrix = { ...matrixData, [cellKey]: value };
                                  onChange(updatedMatrix);
                                }}
                                disabled={disabled}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(field.columnDropdownOptions?.[colIdx]?.split(',') || field.dropdownOptions || []).map((option, optIdx) => (
                                    <SelectItem key={optIdx} value={String(option).trim()}>{String(option).trim()}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : columnType === 'checkbox' ? (
                              <div className="flex justify-center">
                                <input
                                  type="checkbox"
                                  checked={cellValue === 'true' || cellValue === true}
                                  onChange={(e) => {
                                    const updatedMatrix = { ...matrixData, [cellKey]: e.target.checked };
                                    onChange(updatedMatrix);
                                  }}
                                  disabled={disabled}
                                  className="w-4 h-4"
                                />
                              </div>
                            ) : (
                              <AutoGrowTextarea
                                className="w-full min-h-[32px] text-xs resize-none border rounded px-2 py-1"
                                value={cellValue}
                                onChange={(newValue) => {
                                  const updatedMatrix = { ...matrixData, [cellKey]: newValue };
                                  onChange(updatedMatrix);
                                }}
                                placeholder="Enter text"
                                disabled={disabled}
                              />
                            )}
                          </td>
                        );
                      })}
                      {extraRows.length > 0 && (
                      <td className="border p-1">
                        {isExtraRow && (
                          <button
                            type="button"
                            onClick={() => {
                              const newExtraRows = extraRows.filter((_, idx) => idx !== (rowIdx - baseRows.length));
                              const updatedMatrix = { ...matrixData, _extraRows: newExtraRows };
                              const totalRows = baseRows.length + extraRows.length;
                              // Remove this row's cells, then shift later rows' cells up one index
                              columnHeaders.forEach((_, colIdx) => {
                                delete updatedMatrix[`${rowIdx}-${colIdx}`];
                                for (let r = rowIdx + 1; r < totalRows; r++) {
                                  if (`${r}-${colIdx}` in updatedMatrix) {
                                    updatedMatrix[`${r - 1}-${colIdx}`] = updatedMatrix[`${r}-${colIdx}`];
                                    delete updatedMatrix[`${r}-${colIdx}`];
                                  } else {
                                    delete updatedMatrix[`${r - 1}-${colIdx}`];
                                  }
                                }
                              });
                              onChange(updatedMatrix);
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove row"
                            data-testid={`matrix-remove-row-${field.id}-${rowIdx}`}
                            disabled={disabled}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const updatedMatrix = { 
                ...matrixData, 
                _extraRows: [...extraRows, ''] 
              };
              onChange(updatedMatrix);
            }}
            disabled={disabled}
            data-testid={`matrix-add-row-${field.id}`}
          >
            + Add Row
          </Button>
        </div>
      </div>
    );
  }
  
  // === SIGNATURE FIELD ===
  
  if (field.type === 'signature') {
    const rawValue = newNoteFormData[field.id] || '';
    const { loadedSignatures, setLoadedSignatures, getSignatureUrl } = context;
    
    return (
      <div key={index} style={fieldStyle}>
        <SignatureDisplay
          value={rawValue}
          label={field.label}
          required={field.required}
          users={users}
          currentUser={currentUser}
          onChange={!isViewMode ? (val) => onChange(val) : null}
          disabled={isViewMode || disabled}
          testId={`specialty-signature-field-${field.id}`}
          loadedSignatures={loadedSignatures}
          setLoadedSignatures={setLoadedSignatures}
          getSignatureUrl={getSignatureUrl}
        />
      </div>
    );
  }
  
  // === PAN SELECT ===
  
  if (field.type === 'pan_select') {
    const { availablePANs = [] } = context;
    // In edit mode, field.value contains the current value; newNoteFormData may be stale
    // So prefer field.value if it exists, then fall back to newNoteFormData
    const currentValue = field.value !== undefined && field.value !== '' 
      ? field.value 
      : (newNoteFormData[field.id] || '');
    // A value is custom if it exists AND is not in the PAN library
    const isFromLibrary = currentValue && availablePANs.some(p => p.name === currentValue);
    // Show custom textarea if value exists and is NOT from library, OR if user explicitly selected custom
    // We detect "custom mode" by checking if __custom__ marker is in the field's metadata
    const customModeKey = `${field.id}__custom_mode`;
    const isInCustomMode = newNoteFormData[customModeKey] === true;
    const showCustomInput = isInCustomMode || (currentValue && !isFromLibrary);
    
    // View mode
    if (isViewMode) {
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          {currentValue ? (
            <div className="mt-1">
              <ViewPill variant="selected">{currentValue}</ViewPill>
              {!isFromLibrary && <span className="text-xs text-blue-600 ml-2">(Custom)</span>}
            </div>
          ) : (
            <ViewValue />
          )}
        </div>
      );
    }
    
    // Determine select value - use __custom__ if in custom mode, otherwise the actual value
    const selectDisplayValue = showCustomInput ? '__custom__' : (currentValue || '');
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <div className="space-y-2">
          <Select 
            value={selectDisplayValue}
            onValueChange={(value) => {
              void 0 && console.log('[PAN Select] onValueChange called with:', value, 'for field:', field.id);
              if (value === '__custom__') {
                // Enter custom mode - keep existing value if it's custom, otherwise clear
                void 0 && console.log('[PAN Select] Entering custom mode');
                if (context.setNewNoteFormData) {
                  context.setNewNoteFormData(prev => ({
                    ...prev,
                    [customModeKey]: true
                  }));
                }
                // Don't clear the value - let user type in textarea
              } else if (value === '__clear__') {
                // Clear both value and custom mode
                void 0 && console.log('[PAN Select] Clearing value');
                onChange('');
                if (context.setNewNoteFormData) {
                  context.setNewNoteFormData(prev => ({
                    ...prev,
                    [customModeKey]: false
                  }));
                }
              } else {
                // Selected from library - exit custom mode
                void 0 && console.log('[PAN Select] Selected from library:', value);
                onChange(value);
                if (context.setNewNoteFormData) {
                  context.setNewNoteFormData(prev => ({
                    ...prev,
                    [customModeKey]: false
                  }));
                }
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger className={isFromLibrary ? 'border-green-300 bg-green-50' : ''}>
              <SelectValue placeholder="Select a PAN or write custom..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__clear__">
                <span className="text-gray-400">-- Clear selection --</span>
              </SelectItem>
              <SelectItem value="__custom__">
                <span className="text-blue-600 font-medium">✏️ Write custom need...</span>
              </SelectItem>
              {/* Non-safety PANs - alphabetical */}
              {availablePANs
                .filter(pan => !pan.is_safety_related)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((pan) => (
                  <SelectItem key={pan.id} value={pan.name}>
                    {pan.name}
                  </SelectItem>
                ))}
              {/* Divider if there are safety-related PANs */}
              {availablePANs.some(pan => pan.is_safety_related) && (
                <div className="border-t my-1 pt-1">
                  <span className="text-xs text-gray-500 px-2">Safety-Related</span>
                </div>
              )}
              {/* Safety-related PANs at bottom */}
              {availablePANs
                .filter(pan => pan.is_safety_related)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((pan) => (
                  <SelectItem key={pan.id} value={pan.name}>
                    ⚠️ {pan.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {showCustomInput && (
            <DebouncedTextarea 
              value={currentValue}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Write a custom prioritized need..."
              rows={2}
              className="border-blue-200 focus:border-blue-400"
              disabled={disabled}
              debounceMs={200}
            />
          )}
          {isFromLibrary && (
            <p className="text-xs text-green-600">✓ Selected from PAN library</p>
          )}
        </div>
      </div>
    );
  }
  
  // === CURRICULUM SESSION INTERVENTIONS ===
  if (field.type === 'curriculum_session_interventions') {
    const currentValues = Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : [];
    const options = field.options || [];
    
    // View mode
    if (isViewMode) {
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label}</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {currentValues.length > 0 ? (
              currentValues.map((val, idx) => (
                <ViewPill key={idx}>{val}</ViewPill>
              ))
            ) : (
              <span className="text-gray-400 italic text-sm">No interventions selected</span>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <div className="border rounded-md p-2 bg-white max-h-48 overflow-y-auto">
          {options.length > 0 ? (
            options.map((option, idx) => (
              <label key={idx} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 cursor-pointer rounded">
                <input
                  type="checkbox"
                  checked={currentValues.includes(option)}
                  onChange={(e) => {
                    let newValues = [...currentValues];
                    if (e.target.checked) {
                      newValues.push(option);
                    } else {
                      newValues = newValues.filter(v => v !== option);
                    }
                    onChange(newValues);
                  }}
                  disabled={disabled}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))
          ) : (
            <p className="text-sm text-gray-400 italic">No interventions configured</p>
          )}
        </div>
        {currentValues.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">{currentValues.length} selected</p>
        )}
      </div>
    );
  }
  
  // === CONTAINER (wrapper for nested fields) ===
  if (field.type === 'container') {
    return (
      <div key={index} style={fieldStyle} className="border rounded p-3 bg-gray-50">
        <Label>{field.label || 'Container'}</Label>
        <p className="text-xs text-gray-500">Container field (children rendered separately)</p>
      </div>
    );
  }
  
  // Not a specialty field type
  return null;
};

export default renderSpecialtyField;
